import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key } from '@shared/api/query-keys';

import type { CommentId, RemoveCommentResult } from '../_shared/types';
import { moderationQueueRootKey } from './queries';

/**
 * The two decisions this queue takes.
 *
 * **Neither is optimistic and neither auto-retries.** A moderation decision is
 * terminal on one side — `REMOVED` has no path back, not even for its author —
 * and a card that leaves the queue before the server agreed is a claim the
 * operator cannot see through. Both are idempotent server-side behind a
 * predicate guard: a second press meets `409
 * ENGAGEMENT_COMMENT_ALREADY_MODERATED` rather than a second side effect and a
 * second notification.
 *
 * The invalidation set carries the engagement report, because the queue depth is
 * a figure the console entry reports and a dashboard still showing the old one
 * is drift the operator has no way to notice.
 */
function useQueueInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: moderationQueueRootKey() });
    void queryClient.invalidateQueries({ queryKey: key('analytics', 'report', 'engagement') });
  };
}

/**
 * Approve — no dialog anywhere in front of it.
 *
 * Approving sends the comment where its author already meant it to go. A
 * confirmation on a constructive action trains an operator to dismiss
 * confirmations, which is exactly what makes the rejection dialog worthless when
 * it matters.
 */
export function useReleaseComment() {
  const invalidate = useQueueInvalidation();

  return useMutation({
    mutationFn: (commentId: CommentId) =>
      api.post<null>('engagement', `/admin/comments/${commentId}:release`),
    onSuccess: invalidate,
    retry: false,
  });
}

/**
 * Reject — a written reason is **required on the wire**.
 *
 * The owning service refuses a blank one with `422
 * ENGAGEMENT_REMOVAL_REASON_REQUIRED` and writes nothing. The reason is a
 * **record, not a rejection notice**: it is stored beside the deciding operator
 * and the decision's timestamp, and is never returned on any subscriber-facing
 * route. It exists because moderation decisions are recorded in-row and nowhere
 * else in v1 — there is no audit topic to carry them — so a terminal decision
 * with no stated cause is a decision nobody can review.
 *
 * That is **not** the rejection-reason *list* the UI/UX pack rules out. No
 * configured vocabulary is invented here and no picker is drawn: the field is
 * free text the operator writes.
 *
 * The response reports both figures — the whole cascade and the released part of
 * it — so the surface states the effect it applied rather than guessing at it.
 * Only the released rows were ever in the count; the held ones never were.
 */
export function useRemoveComment() {
  const invalidate = useQueueInvalidation();

  return useMutation({
    mutationFn: ({ commentId, reason }: { commentId: CommentId; reason: string }) =>
      api.post<RemoveCommentResult>('engagement', `/admin/comments/${commentId}:remove`, { reason }),
    onSuccess: invalidate,
    retry: false,
  });
}
